// crownpack: the decoder of the crown pack CPK1 (pipeline/crownpack.py,
// APP_FORESTVOL §2a, PROTOCOL.md "Crown pack") — one tile's measured crowns
// (kind 0, 1024 m) or shrubs (kind 1, 512 m), no y (a crown stands on the
// DRAWN ground). Pure (no three.js): trees / shrubs, CanopyGen and the sprite
// workers import it. An unknown magic, version, kind or a malformed body
// THROWS (no per-revision fallback).
//
// header 24 B LE: 'CPK1' · ver u8 · kind u8 · bits_xz u8 · 0 · tile_m u16 ·
// i i16 · j i16 · n u32 · 6 B 0. Body: the sorted Morton keys (x even bits,
// z odd) as LEB128 deltas, then the u8 planes h, r (trees only), appear.
export const CPK_MAGIC = 'CPK1';
export const CPK_VER = 1;
export const CPK_HEADER = 24;
export const CPK_BITS = [12, 11];        // per kind: 0.25 m in either tile
export const CPK_H_PER_M = [8, 50];      // per kind: h byte = h·8 (trees), h·50 (shrubs)
export const CPK_R_M = 0.2;              // trees' r byte = r / 0.2 m (0 = unmeasured stand)

function unspread(v) {
  v &= 0x55555555;
  v = (v | (v >>> 1)) & 0x33333333;
  v = (v | (v >>> 2)) & 0x0f0f0f0f;
  v = (v | (v >>> 4)) & 0x00ff00ff;
  return (v | (v >>> 8)) & 0x0000ffff;
}

// The per-tree hash: the WORLD quantised position (qx, qz: 0.25 m cells,
// x = (qx + 0.5) · quantum) — order-independent and stable under edits;
// every per-tree choice (variant, rotation, species draw, sway) keys on it.
export function crownHash(qx, qz) {
  let h = Math.imul(qx | 0, 0x9e3779b1) ^ Math.imul((qz | 0) + 0x7f4a7c15, 0x85ebca77);
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

// The header alone -> {ver, kind, bitsXZ, tileM, i, j, n, quantum}; throws on
// a foreign magic / version / kind.
export function readCrownHeader(buf) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  if (u8.length < CPK_HEADER) throw new Error(`crownpack: ${u8.length} B is shorter than the header`);
  const magic = String.fromCharCode(u8[0], u8[1], u8[2], u8[3]);
  if (magic !== CPK_MAGIC) throw new Error(`crownpack: magic ${JSON.stringify(magic)} is not ${CPK_MAGIC}`);
  const dv = new DataView(u8.buffer, u8.byteOffset, CPK_HEADER);
  const ver = dv.getUint8(4), kind = dv.getUint8(5), bitsXZ = dv.getUint8(6);
  if (ver !== CPK_VER) throw new Error(`crownpack: version ${ver} (this client reads ${CPK_VER})`);
  if (kind !== 0 && kind !== 1) throw new Error(`crownpack: unknown kind ${kind}`);
  if (bitsXZ !== CPK_BITS[kind]) throw new Error(`crownpack: bits_xz ${bitsXZ} for kind ${kind}`);
  const tileM = dv.getUint16(8, true);
  return { ver, kind, bitsXZ, tileM, i: dv.getInt16(10, true), j: dv.getInt16(12, true),
    n: dv.getUint32(14, true), quantum: tileM / (1 << bitsXZ) };
}

// One tile -> header fields + per record (Morton order): qx / qz (Int32Array,
// WORLD quantised), x / z (Float32Array, game metres at the cell centre),
// h, r (trees; null for shrubs), appear (Uint8Array), hash (Uint32Array).
export function decodeCrowns(buf) {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  const hd = readCrownHeader(u8);
  const { n, kind, bitsXZ, i, j, quantum } = hd;
  const qx = new Int32Array(n), qz = new Int32Array(n);
  const x = new Float32Array(n), z = new Float32Array(n), hash = new Uint32Array(n);
  const ox = i << bitsXZ, oz = j << bitsXZ, kmax = 2 ** (2 * bitsXZ);
  let p = CPK_HEADER, key = 0;
  for (let k = 0; k < n; k++) {
    let d = 0, mul = 1, b;
    do {
      if (p >= u8.length) throw new Error('crownpack: truncated key deltas');
      b = u8[p++];
      d += (b & 0x7f) * mul;
      mul *= 128;
    } while (b & 0x80);
    key += d;
    if (key >= kmax) throw new Error(`crownpack: key ${key} outside the tile`);
    const ax = ox + unspread(key), az = oz + unspread(key >>> 1);
    qx[k] = ax; qz[k] = az;
    x[k] = (ax + 0.5) * quantum; z[k] = (az + 0.5) * quantum;
    hash[k] = crownHash(ax, az);
  }
  const planes = kind === 0 ? 3 : 2;
  if (u8.length - p !== planes * n) throw new Error(`crownpack: body is ${u8.length - p} B after the keys, expected ${planes * n}`);
  const plane = () => { const a = u8.slice(p, p + n); p += n; return a; };
  const h = plane();
  const r = kind === 0 ? plane() : null;
  const appear = plane();
  return { ...hd, qx, qz, x, z, h, r, appear, hash };
}
